/**
 * B1072 — Verrou de passage (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * Corriger une erreur ne suffit pas : la prochaine régénération réécrit tout
 * le texte à partir du transcript et efface la correction. Verrouiller un
 * passage le fait réappliquer automatiquement après chaque régénération.
 *
 * Le verrou est rattaché à la VIDÉO et non à la synthèse : régénérer supprime
 * le document et en crée un autre, un verrou attaché à la synthèse
 * disparaîtrait au moment exact où il devrait servir.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export const LockPassageButton = ({
  videoId,
  lockedText,
  originalText = "",
  note = "",
  onLocked,
  testId,
  compact = false,
}) => {
  const [saving, setSaving] = useState(false);
  const tooShort = (lockedText || "").trim().length < 12;

  const lock = async (e) => {
    e?.stopPropagation();
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/synthesis/locked-passages`,
        { locked_text: lockedText, original_text: originalText, note },
        { withCredentials: true }
      );
      toast.success("Passage verrouillé — il sera réappliqué après chaque régénération.");
      if (onLocked) await onLocked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verrouillage impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={lock}
      disabled={saving || tooShort}
      className={
        compact
          ? "inline-flex items-center gap-1 text-[10px] font-semibold text-amber-900 hover:text-[#002FA7] disabled:opacity-40"
          : "inline-flex items-center gap-1.5 px-2 py-1 rounded border border-zinc-300 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
      }
      data-testid={testId}
      title={
        tooShort
          ? "Passage trop court pour être verrouillé (12 caractères minimum)"
          : "Verrouiller ce passage : la régénération ne pourra plus l'écraser"
      }
    >
      <Lock size={11} weight="bold" />
      {saving ? "Verrouillage…" : "Verrouiller"}
    </button>
  );
};

export const LockedPassagesList = ({ videoId, locks = [], lockReport, onChanged, onOpenTranscript, lang = "fr" }) => {
  const [removing, setRemoving] = useState(null);

  const unlock = async (lockId) => {
    setRemoving(lockId);
    try {
      await axios.delete(
        `${API}/api/videos/${videoId}/synthesis/locked-passages/${lockId}`,
        { withCredentials: true }
      );
      toast.success("Verrou levé.");
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    } finally {
      setRemoving(null);
    }
  };

  const applied = lockReport?.applied || [];
  const missed = lockReport?.missed || [];
  if (!locks.length && !applied.length && !missed.length) return null;

  return (
    <section className="mt-5 not-prose" data-testid="locked-passages">
      <h3 className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
        <Lock size={13} weight="bold" className="text-[#002FA7]" />
        {lang === "fr"
          ? `Passages verrouillés (${locks.length})`
          : `Locked passages (${locks.length})`}
      </h3>
      <p className="text-[11px] text-zinc-500 mt-0.5">
        {lang === "fr"
          ? "Réappliqués automatiquement après chaque régénération de la synthèse."
          : "Automatically re-applied after each synthesis regeneration."}
      </p>

      {(applied.length > 0 || missed.length > 0) && (
        <div
          className="mt-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px]"
          data-testid="lock-report"
        >
          {applied.length > 0 && (
            <p className="text-emerald-800">
              {applied.length} passage(s) verrouillé(s) réappliqué(s) lors de la dernière
              régénération.
            </p>
          )}
          {missed.length > 0 && (
            <p className="text-amber-800" data-testid="lock-report-missed">
              {missed.length} passage(s) verrouillé(s) introuvable(s) après régénération —
              le texte a été reformulé autrement, à vérifier à la main. Réécrire un passage
              qu'on ne reconnaît pas serait plus risqué que l'erreur elle-même.
            </p>
          )}
        </div>
      )}

      <ul className="mt-2 space-y-2">
        {locks.map((l) => (
          <li
            key={l.id}
            className="text-[11px] border-l-2 border-[#002FA7]/40 pl-3"
            data-testid={`locked-passage-${l.id}`}
          >
            <span className="text-zinc-800">« {l.locked_text} »</span>
            <span className="block text-zinc-500">
              {l.locked_by} · {String(l.locked_at || "").slice(0, 10)}
              {l.reapplied_count
                ? ` · réappliqué ${l.reapplied_count} fois`
                : ""}
            </span>
            {/* B1073 (Eric 2026-09-04) — Récidiviste dès la 2e réapplication
                (choix Eric) : ce n'est plus la correction qui pose problème,
                c'est le transcript qui la contredit à chaque régénération. */}
            {(l.reapplied_count || 0) >= 2 && (
              <span
                className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] font-semibold"
                data-testid={`locked-passage-repeat-${l.id}`}
                title="Réappliqué plusieurs fois : le transcript contredit la correction. Corriger la source (transcription ou document) pour arrêter la répétition."
              >
                récidiviste
              </span>
            )}
            <button
              type="button"
              onClick={() => unlock(l.id)}
              disabled={removing === l.id}
              className="mt-0.5 text-[10px] font-semibold text-zinc-500 hover:text-red-700 disabled:opacity-40"
              data-testid={`unlock-passage-${l.id}`}
            >
              {removing === l.id ? "Levée…" : "Lever le verrou"}
            </button>
            {/* B1108 (Eric 2026-09-08) — Un verrou traite le SYMPTÔME : la
                synthèse est corrigée, mais le transcript continue de dire le
                contraire à chaque régénération. Ce bouton ouvre la
                transcription de la même vidéo avec le passage verrouillé déjà
                dans la recherche, pour corriger la SOURCE et faire cesser la
                répétition. Proposé sur tous les verrous (choix Eric), pas
                seulement les récidivistes. */}
            {onOpenTranscript && (
              <button
                type="button"
                onClick={() => onOpenTranscript(l.locked_text)}
                className="mt-0.5 ml-3 text-[10px] font-semibold text-[#002FA7] hover:underline"
                data-testid={`fix-source-passage-${l.id}`}
                title="Ouvrir la transcription sur ce passage pour corriger la source (le verrou traite le symptôme, pas la cause)"
              >
                Corriger la source
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default LockPassageButton;
